<template>
    <DialogFullSize
        ref="importPopup"
        class="import-popup"
        :class="{ 'flatfile-step': getCurrentStepId === 4 }"
        :title="title"
        @handleClose="closed"
    >
        <div slot="content" class="content">
            <ImportChoiceTypeStep
                v-if="getCurrentStepId === 1"
                class="step-content"
                @onClickContinue="onClickContinue"
            />
            <ImportPrepareStep v-if="getCurrentStepId === 2" class="step-content" />
            <div v-if="getCurrentStepId === 3" class="setup-import">
                <ImportSetupStep class="step-content" />
            </div>
            <div v-if="getCurrentStepId === 4" class="setup-import import-full-screen">
                <ImportFlatFileStepVue @closed="closed(true)" @onError="updateCurrentStepId(3)" />
            </div>
        </div>
        <div v-if="getCurrentStepId !== 4" slot="footer" class="footer">
            <div
                v-if="getCurrentStepId !== 1"
                class="import-popup__btn-back cpointer"
                @click="onClickBackward"
            >
                {{ $l10n.__('Retour') }}
            </div>
            <CompassButton
                :disabled="!canContinue"
                :label="labelContinueBtn"
                size="large"
                class="ml-3"
                @click="onClickContinue"
            />
        </div>
    </DialogFullSize>
</template>

<script>
import ImportChoiceTypeStep from 'vcomponents/settings/import/ImportChoiceTypeStep.vue';
import ImportSetupStep from 'vcomponents/settings/import/ImportSetupStep.vue';
import ImportFlatFileStepVue from 'vcomponents/settings/import/ImportFlatFileStep.vue';
import { createNamespacedHelpers } from 'vuex';

const importsStore = createNamespacedHelpers('importsStore');
import ImportPrepareStep from 'vcomponents/settings/import/ImportPrepareStep.vue';
import DialogFullSize from 'vcomponents/utilities/DialogFullSize.vue';
import importsMixins from 'vcomponents/settings/import/importsMixins.js';

export default {
    components: {
        DialogFullSize,
        ImportChoiceTypeStep,
        ImportSetupStep,
        ImportFlatFileStepVue,
        ImportPrepareStep,
    },
    mixins: [importsMixins],
    props: {
        isRestarted: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showAlertOnClose: false,
        };
    },
    computed: {
        ...importsStore.mapGetters([
            'getTypeImport',
            'getImportSteps',
            'getCurrentStepId',
            'getCurrentSubStep',
            'getCurrentSubStepId',
            'getCurrentStep',
            'getHasCustomFields',
            'getHasDataForSetupStep',
        ]),
        canContinue() {
            if (this.getCurrentStepId === 2 && !this.getHasDataForSetupStep) {
                return false;
            }
            if (this.getCurrentSubStep) {
                return this.getCurrentSubStep.isValid;
            }
            if (this.getCurrentStepId !== 1) {
                return true;
            }
            return this.getTypeImport !== null;
        },
        title() {
            return this.getImportSteps.find((step) => step.id === this.getCurrentStepId).title;
        },
        labelContinueBtn() {
            if (this.getCurrentStepId === 3) {
                const isLastSubStep = this.getCurrentSubStepId === this.lengthSubSteps;
                if (isLastSubStep) {
                    return this.$l10n.__('Importer mon fichier');
                } else {
                    return `Continuer (${this.getCurrentSubStepId}/${this.lengthSubSteps})`;
                }
            }
            return this.$l10n.__('Continuer');
        },
        lengthSubSteps() {
            return this.getHasCustomFields
                ? this.getCurrentStep.subSteps.length
                : this.getCurrentStep.subSteps.length - 1;
        },
    },
    watch: {
        getTypeImport: {
            handler(oldType, newType) {
                if (oldType && oldType !== newType) {
                    this.resetSubStepsValidity();
                }
            },
            deep: true,
        },
    },
    created() {
        this.updateLoadingMetadata(true);
        this.initImportMetadata()
            .then(() => {
                this.updateLoadingMetadata(false);
            })
            .catch(() => {
                this.$pushToast({
                    type: 'error',
                    text: this.$t.__('settings.import.ImportPopup.error'),
                    icon: true,
                });
                this.closed();
            });
        if (this.isRestarted) {
            this.updateCurrentStepId(4);
        }
    },
    mounted() {
        // we prevent the go back from the browser
        history.pushState(null, document.title, location.href);
        window.addEventListener('popstate', this.onPopstate);
    },
    destroyed() {
        window.removeEventListener('popstate', this.onPopstate);
        if (this.$route.query && Object.keys(this.$route.query).length !== 0) {
            this.$router.replace({ query: {} });
        }
        this.resetImport();
    },
    methods: {
        ...importsStore.mapActions([
            'initImportMetadata',
            'resetDefaultOptions',
            'updateIsRestartImportNeed',
            'updateImportOptions',
            'updateLoadingMetadata',
            'updateCurrentStepId',
            'updateCurrentSubStepId',
            'updateTypeImport',
            'resetImport',
            'resetSubStepsValidity',
            'updateHasDataForSetupStep',
        ]),
        onClickContinue() {
            if (this.getCurrentStepId === 1) {
                if (this.getTypeImport?.isNew) {
                    this.updateImportOptions();
                } else {
                    this.openLegacyImport(this.getTypeImport?.type);
                    return;
                }
            }
            if (this.getCurrentSubStep && this.getCurrentSubStepId !== this.lengthSubSteps) {
                this.updateCurrentSubStepId(this.getCurrentSubStep.id + 1);
            } else {
                this.updateCurrentStepId(this.getCurrentStepId + 1);
                this.updateCurrentSubStepId(null);
            }
        },
        onClickBackward() {
            this.updateHasDataForSetupStep(true);
            if (this.getCurrentSubStep && this.getCurrentSubStep.id !== 1) {
                this.updateCurrentSubStepId(this.getCurrentSubStep.id - 1);
            } else {
                this.updateCurrentStepId(this.getCurrentStepId - 1);
                this.updateCurrentSubStepId(null);
            }
        },
        closed(isCompleted = false) {
            this.$emit('onClose', isCompleted);
        },
        onPopstate() {
            history.pushState(null, document.title, location.href);
        },
    },
};
</script>
<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.import-popup-cancel-import {
    z-index: 1001;
}

.dialog-full-size.import-popup.flatfile-step {
    .header > h1 {
        display: none;
    }
}

.import-popup__btn-back {
    font-size: 17px;
    font-weight: 600;
    color: $primary-50;
    place-self: center;
    padding: 16px;
    transition: all 100ms cubic-bezier(0, 0, 0.3, 1);
}

.import-popup__btn-back:hover {
    background-color: $primary-05;
    border-radius: 4px;
}

.setup-import {
    &.import-full-screen {
        max-width: none !important;
    }
}

#compass-dropdown-container {
    .menu-import-dialog {
        z-index: 1001;
    }
}

#compass-tooltip-container {
    .tooltip-import-popup {
        white-space: pre-line;
        z-index: 1002;
    }
}
</style>
